import { baseApi } from '../../../core/baseApi';
import { messagesLogger } from '@/core/utils/logger';
import {
  createUpdateMutation,
  createDeleteMutation,
  unwrapApiResponse
} from '../../../patterns/crud';
import { Message, GetMessagesParams, SendMessageData, MessagesResponse } from '../types';
import { compareMessagesByTimestamp, normalizeMessage, type RawMessageInput } from '../utils/normalize';
import { MessageConversationIDs } from '../types/mutation';
import { MESSAGES_CACHE_PARAMS, TEMP_MESSAGE_TTL_MS } from '../contracts/messaging-contracts';
import { sendMessageOptimistic, setMessagesApiRef } from './send-message-optimistic';
// Re-export for backward compatibility — consumers should migrate to blob-lifecycle.ts
export { revokeConversationBlobUrls } from '../utils/blob-lifecycle';

const buildClientMessageId = (conversationId: string) =>
  `client_${conversationId}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// Re-export from contracts for backward compatibility — consumers should
// gradually migrate to importing directly from contracts/messaging-contracts.
export { MESSAGES_CACHE_PARAMS } from '../contracts/messaging-contracts';

export const messagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMessagesForConversation: builder.query<Message[], GetMessagesParams>({
      query: (params) => {
        const { parentId, ...queryParams } = params;
        if (!parentId || parentId === '__COMPOSE__' || parentId === 'undefined' || parentId === 'null') {
          throw new Error(`Invalid conversation ID: ${parentId || 'empty'}. Cannot fetch messages.`);
        }
        const url = `/messages/conversation/${parentId}`;
        return {
          url,
          params: queryParams
        };
      },
      // Messages are real-time; keep endpoint cache tighter than global
      keepUnusedDataFor: 300,
      transformResponse: (response: MessagesResponse | { data: MessagesResponse } | Message[]): Message[] => {
        let messages: unknown[] = [];

        if ('data' in response && response.data && 'messages' in response.data) {
          messages = response.data.messages;
        } else if ('messages' in response) {
          messages = response.messages;
        } else if (Array.isArray(response)) {
          messages = response;
        }

        return messages.map((msg): Message => normalizeMessage(msg as RawMessageInput));
      },
      // Preserve in-flight optimistic messages during refetches.
      // With inline send, temp messages are replaced within ~2s. Only preserve
      // temp messages younger than 10s (in-flight) — everything else uses server data.
      merge: (currentCacheData, responseData) => {
        const now = Date.now();

        const inFlightTemps = currentCacheData.filter((msg) => {
          if (typeof msg.id !== 'string' || !msg.id.startsWith('temp_')) return false;
          // Always preserve failed temp messages — the user needs to see the error + retry button.
          // These are never on the server (API failed), so they'd be silently dropped without this.
          if (msg.status === 'failed') return !responseData.some((r) => r.id === msg.id);
          const msgTs = msg.timestamp || (msg.sentAt ? new Date(msg.sentAt).getTime() : 0);
          if (msgTs && now - msgTs > TEMP_MESSAGE_TTL_MS) return false;
          return !responseData.some((r) => r.id === msg.id);
        });

        if (inFlightTemps.length === 0) return responseData;

        const merged = [...responseData, ...inFlightTemps];
        merged.sort(compareMessagesByTimestamp);
        return merged;
      },
      // Never force a refetch — defer to refetchOnMountOrArgChange (30s) at the hook level.
      // IMPORTANT: forceRefetch returning `true` OVERRIDES refetchOnMountOrArgChange,
      // so returning `true` here would bypass the 30s staleness window. We always return
      // `false` so the hook-level setting controls refetch timing. The compose handler's
      // upsertQueryData sets fulfilledTimeStamp, which the 30s check respects.
      forceRefetch: () => false,
      providesTags: (result, error, args) => {
        const { parentId } = args;
        return result
          ? [
              { type: 'Messages' as const, id: parentId },
              ...result.map((msg) => ({ type: 'Messages' as const, id: msg.id }))
            ]
          : [{ type: 'Messages' as const, id: parentId }];
      }
    }),
    sendMessage: builder.mutation<Message, { data: SendMessageData }>({
      query: ({ data }) => {
        if (!data.clientMessageId) {
          data.clientMessageId = buildClientMessageId(data.conversationId);
        }

        // JSON path: presigned uploads OR text-only (no raw files to upload)
        const hasRawFiles = data.files && data.files.length > 0;
        if (!hasRawFiles) {
          return {
            url: '/messages',
            method: 'POST',
            body: {
              conversationId: data.conversationId,
              content: (data.content ?? '').trim(),
              ...(data.messageType ? { messageType: data.messageType } : {}),
              ...(data.deliveryChannel ? { deliveryChannel: data.deliveryChannel } : {}),
              ...(data.recipientId ? { recipientId: data.recipientId } : {}),
              ...(data.to ? { to: data.to } : {}),
              ...(data.clientMessageId ? { clientMessageId: data.clientMessageId } : {}),
              ...(data.tempFileKeys && data.tempFileKeys.length > 0
                ? { tempFileKeys: data.tempFileKeys }
                : {}),
            },
          };
        }

        // FormData path (multer upload — used by mobile and as fallback when presigned fails)
        const formData = new FormData();

        formData.append('conversationId', data.conversationId);
        formData.append('content', (data.content ?? '').trim());

        if (data.messageType) formData.append('messageType', data.messageType);
        if (data.deliveryChannel) formData.append('deliveryChannel', data.deliveryChannel);
        if (data.recipientId) formData.append('recipientId', data.recipientId);
        if (data.to) formData.append('to', data.to);
        if (data.clientMessageId) formData.append('clientMessageId', data.clientMessageId);

        if (data.files && data.files.length > 0) {
          data.files.forEach((file: File) => {
            formData.append('files', file);
          });
        }

        return {
          url: '/messages',
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: Record<string, unknown>): Message => {
        const msg = (response.data || response) as Record<string, unknown>;
        const attachments = msg.attachments as unknown[] | undefined;
        const mediaUrls = msg.media_urls as string[] | undefined;
        const sender = msg.sender as { id?: string; type?: string; [k: string]: unknown } | undefined;
        const normalizedAttachments = (Array.isArray(attachments) && attachments.length > 0)
          ? attachments
          : Array.isArray(mediaUrls)
            ? mediaUrls.map((url: string, idx: number) => ({
                id: `media_${String(msg.id)}_${idx}`,
                type: 'image',
                url,
                name: url.split('/').pop() || `media_${idx}`,
                size: 0,
              }))
            : [];

        return {
          id: msg.id,
          conversationId: msg.conversationId,
          senderId: sender?.id || msg.senderId || '',
          senderType: sender?.type || msg.senderType || 'user',
          content: msg.content || '',
          messageType: msg.messageType || 'text',
          deliveryChannel: msg.deliveryChannel || 'internal',
          timestamp: msg.timestamp || (msg.sentAt ? new Date(msg.sentAt as string).getTime() : (typeof window !== 'undefined' ? Date.now() : 0)),
          sentAt: msg.sentAt,
          status: msg.status || 'sent',
          isLogged: msg.isLogged || false,
          attachments: normalizedAttachments,
          metadata: msg.metadata || {},
          sender
        } as Message;
      },
      // Optimistic update lifecycle delegated to send-message-optimistic.ts
      async onQueryStarted(args, api) {
        return sendMessageOptimistic(args, api);
      }
    }),
    updateMessage: createUpdateMutation('/messages', 'Messages')(builder),
    updateMessageConversation: builder.mutation<unknown, MessageConversationIDs>({
      query: ({ messageId, conversationId }) => ({
        url: '/messages/update-conversation',
        method: 'PUT',
        // Only send what backend expects
        body: { messageId, conversationId }
      }),
      invalidatesTags: (result, error, { conversationId, sourceConversationId }) => [
        { type: 'Messages', id: 'LIST' },
        { type: 'Messages', id: conversationId },
        { type: 'Messages', id: sourceConversationId },
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: 'INFINITE-LIST' }
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { messageId, conversationId, sourceConversationId } = args;
        let movedMessage: Message | undefined;

        const sourceConversationPatch = dispatch(
          messagesApi.util.updateQueryData('getMessagesForConversation',
            { parentId: sourceConversationId, ...MESSAGES_CACHE_PARAMS },
            (draft) => {
              const messageIndex = draft.findIndex(msg => msg.id === messageId);
              if (messageIndex !== -1) {
                movedMessage = { ...draft[messageIndex] };
                draft.splice(messageIndex, 1);
              }
            }
          )
        );

        let destinationConversationPatch: { undo: () => void } | null = null;
        if (movedMessage) {
          const confirmedMessage = movedMessage;
          const patchResult = dispatch(
            messagesApi.util.updateQueryData('getMessagesForConversation',
              { parentId: conversationId, ...MESSAGES_CACHE_PARAMS },
              (draft) => {
                const updatedMessage: Message = {
                  ...confirmedMessage,
                  conversationId: conversationId
                };
                const existingIndex = draft.findIndex(m => m.id === messageId);
                if (existingIndex === -1) {
                  draft.push(updatedMessage);
                  draft.sort(compareMessagesByTimestamp);
                }
              }
            )
          );
          if (patchResult.patches.length > 0) {
            destinationConversationPatch = patchResult;
          }
        }

        try {
          await queryFulfilled;
        } catch (error) {
          messagesLogger.error('[MoveMessage] Move failed, reverting optimistic updates:', error);
          sourceConversationPatch.undo();
          if (destinationConversationPatch) {
            destinationConversationPatch.undo();
          }
        }
      }
    }),
    deleteMessage: createDeleteMutation('/messages', 'Messages')(builder),
    markMessageRead: createUpdateMutation('/messages/read', 'Messages')(builder),
    retryMessage: builder.mutation<Message, { messageId: string }>({
      query: ({ messageId }) => ({
        url: `/messages/retry/${messageId}`,
        method: 'POST'
      }),
      transformResponse: (response: Record<string, unknown>): Message => {
        const msg = (response.data || response) as Record<string, unknown>;
        return normalizeMessage(msg as RawMessageInput);
      },
      // Invalidate both the individual message tag AND the conversation-level
      // tag so the entire message list is refetched with the updated status.
      invalidatesTags: (result, _error, { messageId }) => {
        const tags: Array<{ type: 'Messages'; id: string }> = [
          { type: 'Messages', id: messageId },
        ];
        if (result?.conversationId) {
          tags.push({ type: 'Messages', id: result.conversationId });
        }
        return tags;
      },
    }),
    getLoggingEvents: builder.query<
      { events: Array<{
        logEventId: string;
        conversationId: string;
        contactName: string;
        title: string | null;
        messageCount: number;
        logType: 'text' | 'link' | null;
        destination: string;
        status: 'success' | 'failure';
        occurredAt: string;
        errorMessage?: string;
        errorCode?: string;
        messages?: Array<{
          id: string;
          content: string;
          senderName: string;
          senderType: 'user' | 'contact';
          sentAt: string;
        }>;
      }>; total: number },
      { status?: 'all' | 'success' | 'failure'; page?: number; limit?: number; since?: string }
    >({
      query: ({ status = 'all', page = 0, limit = 20, since }) => ({
        url: '/messages/logging/events',
        params: { status, page, limit, ...(since ? { since } : {}) }
      }),
      transformResponse: unwrapApiResponse<{ events: Array<{
        logEventId: string;
        conversationId: string;
        contactName: string;
        title: string | null;
        messageCount: number;
        logType: 'text' | 'link' | null;
        destination: string;
        status: 'success' | 'failure';
        occurredAt: string;
        errorMessage?: string;
        errorCode?: string;
        messages?: Array<{
          id: string;
          content: string;
          senderName: string;
          senderType: 'user' | 'contact';
          sentAt: string;
        }>;
      }>; total: number }>,
      providesTags: (_result) => [
        { type: 'Messages' as const, id: 'LOGGING_EVENTS' }
      ]
    }),
  })
});

// Wire up the late-bound reference so send-message-optimistic.ts can access messagesApi
// without a circular import (messages.ts → send-message-optimistic.ts → messages.ts).
setMessagesApiRef(messagesApi);

export const {
  useGetMessagesForConversationQuery,
  useSendMessageMutation,
  useUpdateMessageMutation,
  useUpdateMessageConversationMutation,
  useDeleteMessageMutation,
  useMarkMessageReadMutation,
  useRetryMessageMutation,
  useGetLoggingEventsQuery,
} = messagesApi;